/**
 * /app/{tenantSlug}/loans/new — Request a loan or advance (migration 064).
 *
 * Employee-facing form. The "advance" toggle hides + forces tenure to 1
 * (an advance is a loan with tenure 1; the backend also forces it). The
 * live EMI preview calls POST /loans/preview-schedule — the SAME backend
 * engine that generates the real schedule on disburse, so the preview is
 * the single source of truth (no client-side schedule maths).
 *
 * §17(2): an interest-free loan over ₹20,000 may attract a perquisite —
 * the preview surfaces the disclaimer (v1 flags it, does not compute it).
 */

import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { useCreateLoan, usePreviewSchedule } from "@/hooks/use-loans";
import type { LoanKind, PreviewInstallment } from "@/lib/api/loans";
import { formatPaise, rupeesToPaise } from "@/lib/expense-format";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatDuePeriod } from "./app.$tenantSlug.loans.index";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/loans/new")({
  component: NewLoanRoute,
});

function NewLoanRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const navigate = useNavigate();
  const create = useCreateLoan();
  const preview = usePreviewSchedule();

  const [kind, setKind] = useState<LoanKind>("loan");
  const [principalStr, setPrincipalStr] = useState("");
  const [tenure, setTenure] = useState("6");
  const [reason, setReason] = useState("");
  const [rows, setRows] = useState<PreviewInstallment[]>([]);
  const [perquisite, setPerquisite] = useState(false);

  const effectiveTenure = kind === "advance" ? 1 : Number(tenure) || 0;
  const principalPaise = rupeesToPaise(principalStr);

  // Debounced live preview off the backend engine.
  useEffect(() => {
    if (principalPaise == null || principalPaise <= 0 || effectiveTenure < 1) {
      setRows([]);
      setPerquisite(false);
      return;
    }
    const handle = setTimeout(() => {
      preview
        .mutateAsync({
          principal_paise: principalPaise,
          tenure_months: effectiveTenure,
        })
        .then((res) => {
          setRows(res.installments);
          setPerquisite(res.perquisite_disclaimer);
        })
        .catch(() => {
          setRows([]);
        });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [principalPaise, effectiveTenure]);

  async function submit() {
    if (principalPaise == null || principalPaise <= 0) {
      toast.error("Enter a valid principal amount");
      return;
    }
    if (effectiveTenure < 1) {
      toast.error("Tenure must be at least 1 month");
      return;
    }
    try {
      const loan = await create.mutateAsync({
        kind,
        principal_paise: principalPaise,
        tenure_months: effectiveTenure,
        reason: reason || undefined,
      });
      toast.success(kind === "advance" ? "Advance request submitted." : "Loan request submitted.");
      void navigate({
        to: "/app/$tenantSlug/loans/$loanId",
        params: { tenantSlug, loanId: String(loan.id) },
      });
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <MeModuleShell
      title={kind === "advance" ? "Request an Advance" : "Request a Loan"}
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/me/loans" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-2">
              <Label>Type</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={kind === "loan" ? "secondary" : "outline"}
                  onClick={() => setKind("loan")}
                >
                  Loan (EMIs)
                </Button>
                <Button
                  type="button"
                  variant={kind === "advance" ? "secondary" : "outline"}
                  onClick={() => setKind("advance")}
                >
                  Advance (Next Payroll)
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="principal">Principal (₹)</Label>
              <Input
                id="principal"
                inputMode="decimal"
                placeholder="50000.00"
                value={principalStr}
                onChange={(e) => setPrincipalStr(e.target.value)}
              />
            </div>

            {kind === "loan" && (
              <div className="space-y-2">
                <Label htmlFor="tenure">Tenure (Months)</Label>
                <Input
                  id="tenure"
                  type="number"
                  min={1}
                  max={120}
                  value={tenure}
                  onChange={(e) => setTenure(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Textarea
                id="reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Festival advance, medical emergency, relocation…"
              />
            </div>

            {perquisite && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Interest-free loans over ₹20,000 may attract a §17(2)
                  perquisite (imputed at SBI rates). This is informational —
                  consult your CA. The platform does not compute the
                  perquisite in v1.
                </AlertDescription>
              </Alert>
            )}

            <Button onClick={submit} disabled={create.isPending} className="gap-1.5">
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit Request
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {kind === "advance" ? "Recovery" : "EMI Schedule Preview"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {kind === "advance" ? (
              // An advance has no tenure or EMIs: it is recovered in one
              // deduction from the next payroll (2026-10 final re-check).
              <p className="text-sm text-muted-foreground" data-testid="advance-recovery-preview">
                {rows.length > 0
                  ? `Recovered in one deduction of ${formatPaise(rows[0].AmountPaise)} from your ${formatDuePeriod(rows[0].DueYear, rows[0].DueMonth)} pay.`
                  : "An advance is recovered in one deduction from your next payroll after it is paid out."}
              </p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Enter a principal and tenure to preview the schedule.
              </p>
            ) : (
              <ul className="space-y-1 text-sm">
                {rows.map((r) => (
                  <li
                    key={r.SeqNo}
                    className="flex justify-between border-b py-1 last:border-0"
                  >
                    <span className="text-muted-foreground">
                      #{r.SeqNo} · {formatDuePeriod(r.DueYear, r.DueMonth)}
                    </span>
                    <span className="font-medium">{formatPaise(r.AmountPaise)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </MeModuleShell>
  );
}
